import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Blackout } from "../lib/api.ts";
import { formatLongDate, formatTime, todayKey } from "../lib/format.ts";

interface BlackoutsEditorProps {
  token: string;
  onUnauthorised: () => void;
}

/**
 * Holidays and one-off closures.
 *
 * Leaving both times blank closes the whole range of days, which is what a
 * holiday almost always is. Filling them in closes part of a single day - a
 * dentist appointment on a Tuesday afternoon.
 */
export function BlackoutsEditor({
  token,
  onUnauthorised,
}: BlackoutsEditorProps) {
  const [blackouts, setBlackouts] = useState<Blackout[] | null>(null);
  const [startDate, setStartDate] = useState(() => todayKey());
  const [endDate, setEndDate] = useState(() => todayKey());
  const [partialDay, setPartialDay] = useState(false);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("13:00");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.admin.getBlackouts(token);
      setBlackouts(result.blackouts);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError("Closures could not be loaded.");
    }
  }, [token, onUnauthorised]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.admin.createBlackout(token, {
        startDate,
        endDate,
        ...(partialDay ? { startTime, endTime } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setMessage("Closure added. Those times are no longer bookable.");
      setReason("");
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The closure could not be added.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(blackout: Blackout) {
    if (
      !window.confirm(
        "Remove this closure and make those times bookable again?",
      )
    )
      return;
    setError(null);
    try {
      await api.admin.deleteBlackout(token, blackout.id);
      setMessage("Closure removed.");
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError("The closure could not be removed.");
    }
  }

  /** A closure landing on local midnight at both ends is a whole-day one. */
  function describe(blackout: Blackout): string {
    const startsMidnight = formatTime(blackout.startUtc) === "00:00";
    const endsMidnight = formatTime(blackout.endUtc) === "00:00";

    if (startsMidnight && endsMidnight) {
      const lastDay = new Date(
        new Date(blackout.endUtc).getTime() - 60_000,
      ).toISOString();
      const first = formatLongDate(blackout.startUtc);
      const last = formatLongDate(lastDay);
      return first === last ? first : `${first} - ${last}`;
    }
    return `${formatLongDate(blackout.startUtc)}, ${formatTime(blackout.startUtc)}–${formatTime(blackout.endUtc)}`;
  }

  return (
    <section className="page stack-lg">
      <div>
        <h2 className="admin__section-title">Closures</h2>
        <p className="field-hint">
          Holidays, training days and any other time the studio is shut.
          Existing bookings inside a new closure are not cancelled - check the
          diary first.
        </p>
      </div>

      <form className="panel admin-form" onSubmit={create}>
        <h3 className="admin-form__title">Add a closure</h3>

        <div className="admin-form__grid">
          <div className="field">
            <label htmlFor="bo-start">First day closed</label>
            <input
              id="bo-start"
              type="date"
              className="input"
              value={startDate}
              required
              onChange={(event) => {
                setStartDate(event.target.value);
                // Keep the range valid without making the user fix it.
                if (event.target.value > endDate)
                  setEndDate(event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label htmlFor="bo-end">Last day closed</label>
            <input
              id="bo-end"
              type="date"
              className="input"
              value={endDate}
              min={startDate}
              required
              disabled={partialDay}
              onChange={(event) => setEndDate(event.target.value)}
            />
            {partialDay && (
              <p className="field-hint">
                A part-day closure covers one day only.
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="bo-reason">Reason (optional)</label>
            <input
              id="bo-reason"
              className="input"
              value={reason}
              maxLength={200}
              placeholder="Holiday, training, appointment…"
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>

        <label className="hours-editor__toggle">
          <input
            type="checkbox"
            checked={partialDay}
            onChange={(event) => {
              setPartialDay(event.target.checked);
              if (event.target.checked) setEndDate(startDate);
            }}
          />
          <span>Only part of the day</span>
        </label>

        {partialDay && (
          <div className="hours-editor__times">
            <input
              type="time"
              className="input"
              value={startTime}
              step={900}
              aria-label="Closure start time"
              onChange={(event) => setStartTime(event.target.value)}
            />
            <span aria-hidden="true">–</span>
            <input
              type="time"
              className="input"
              value={endTime}
              step={900}
              aria-label="Closure end time"
              onChange={(event) => setEndTime(event.target.value)}
            />
          </div>
        )}

        {error && <p className="notice notice--error">{error}</p>}
        {message && <p className="notice notice--success">{message}</p>}

        <button type="submit" className="btn" disabled={busy}>
          {busy ? <span className="spinner" /> : "Add closure"}
        </button>
      </form>

      <div>
        <h3 className="admin__section-title">Upcoming closures</h3>
        {!blackouts && (
          <p className="booking__loading">
            <span className="spinner" /> Loading…
          </p>
        )}
        {blackouts?.length === 0 && (
          <p className="notice">No closures booked in.</p>
        )}
        {blackouts && blackouts.length > 0 && (
          <ul className="closure-list">
            {blackouts.map((blackout) => (
              <li key={blackout.id} className="closure">
                <div>
                  <p className="closure__when">{describe(blackout)}</p>
                  {blackout.reason && (
                    <p className="closure__reason">{blackout.reason}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => remove(blackout)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
